import { supabase } from './supabase';
import { ModulePermissions } from '../types/database';

// ─── Phase 76.2.D — module role grant types ──────────────────────────────────
/**
 * A single (module, level) grant emitted by the dynamic <PermissionsMatrix />.
 * Persisted as one row in `company_member_module_roles_v76` keyed by
 *   { member_id, module_key, role_id }
 * where `role_id` is the uuid PK of the corresponding row in
 * `module_roles_v76` whose canonical `role_key` follows the seed convention
 * `${module}.${level}` (e.g. 'cashflow.viewer', 'hr.editor',
 * 'inventory.admin', 'package.admin'). The text role_key is therefore
 * resolved to its uuid at write time and JOINed back at read time
 * (Phase 76.2.E hotfix). Module keys are intentionally typed as `string`
 * (not a closed union) so newly-seeded dictionary rows in
 * `module_roles_v76` — such as Phase 76.2.H's `package.admin` — flow
 * through this layer without requiring a client release (Phase 76.2.H
 * parity).
 *
 * Level 'none' is encoded as ABSENCE of a row — callers should filter those
 * out before passing the array to `replaceMemberModuleRoles()`.
 */
export type ModuleRoleLevel = 'admin' | 'editor' | 'viewer' | 'manager' | 'employee';

export interface ModuleRoleGrant {
  module_key: string;
  level:      ModuleRoleLevel;
}

/**
 * Convert a (module, level) grant into the canonical `module_roles_v76.role_key`.
 * Used as the lookup key to resolve a role_id uuid at write time.
 */
export function moduleRoleKeyFor(moduleKey: string, level: ModuleRoleLevel): string {
  return `${moduleKey}.${level}`;
}

/**
 * Lookup map: `${module_key}.${level}` text  →  `module_roles_v76.id` uuid.
 * Built once per write call via a single SELECT against the role catalog.
 */
type RoleIdLookup = Map<string, string>;

async function buildRoleIdLookup(roleKeys: string[]): Promise<{
  data:  RoleIdLookup | null;
  error: any;
}> {
  if (roleKeys.length === 0) {
    return { data: new Map(), error: null };
  }
  const { data, error } = await supabase
    .from('module_roles_v76')
    .select('id, role_key')
    .in('role_key', roleKeys);
  if (error) return { data: null, error };
  const map: RoleIdLookup = new Map();
  for (const row of (data ?? []) as Array<{ id: string; role_key: string }>) {
    map.set(row.role_key, row.id);
  }
  return { data: map, error: null };
}

/**
 * Translate a `PermissionsMatrixValue`-shaped record (keyed by module_key,
 * values 'admin'|'editor'|'viewer'|'none') into the array of grants ready
 * for persistence. `'none'` cells are dropped.
 */
export function grantsFromMatrixValue(
  value: Record<string, ModuleRoleLevel | 'none'>,
): ModuleRoleGrant[] {
  const out: ModuleRoleGrant[] = [];
  for (const [mod, lvl] of Object.entries(value ?? {})) {
    if (lvl && lvl !== 'none') out.push({ module_key: mod, level: lvl });
  }
  return out;
}

export interface TeamMember {
  id: string;
  user_id: string;
  role: 'admin' | 'member' | 'viewer';
  module_permissions: ModulePermissions | null;
  created_at: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
  profiles: {
    id: string;
    email: string | null;
    full_name: string | null;
    avatar_url: string | null;
    phone: string | null;
  } | null;
  /** Phase 76: canonical module grants keyed by module_key -> role_key suffix. */
  module_grants?: Record<string, ModuleRoleLevel>;
}

export interface InviteTeamMemberData {
  company_id: string;
  email: string;
  role: 'admin' | 'member';
  /**
   * Phase 76.2.D: canonical grants destined for company_member_module_roles_v76.
   * The legacy `module_permissions` JSON shape is no longer accepted — callers
   * must translate via `grantsFromMatrixValue()` before reaching this layer.
   */
  module_role_grants?: ModuleRoleGrant[];
}

export const teamService = {
  // Get team members for a company, with Phase 76 module grants included.
  async getTeamMembers(companyId: string) {
    try {
      const membersResult = await supabase
        .from('company_members')
        .select(`
          id,
          user_id,
          role,
          module_permissions,
          created_at,
          profiles:user_id(id, email, full_name, avatar_url, phone)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (membersResult.error) {
        console.error('Get team members error:', membersResult.error);
        throw membersResult.error;
      }

      if (!membersResult.data || membersResult.data.length === 0) {
        return { data: [], error: null };
      }

      const memberIds = membersResult.data.map((m: any) => m.id);
      const grantsResult = await supabase
        .from('company_member_module_roles_v76')
        .select('member_id, module_key, module_roles_v76:role_id(role_key)')
        .in('member_id', memberIds);

      // Build lookup: member_id -> { module_key -> role_key_suffix }
      const grantsMap: Record<string, Record<string, ModuleRoleLevel>> = {};
      for (const row of (grantsResult.data ?? []) as any[]) {
        const memberId = row.member_id as string;
        const mod      = row.module_key as string;
        const embed    = row.module_roles_v76;
        const roleK    = Array.isArray(embed) ? embed[0]?.role_key ?? '' : embed?.role_key ?? '';
        const suffix   = roleK.startsWith(`${mod}.`) ? roleK.slice(mod.length + 1) : roleK;
        if (suffix === 'admin' || suffix === 'editor' || suffix === 'viewer' || suffix === 'manager' || suffix === 'employee') {
          if (!grantsMap[memberId]) grantsMap[memberId] = {};
          grantsMap[memberId][mod] = suffix as ModuleRoleLevel;
        }
      }

      const members = (membersResult.data as any[]).map((member) => ({
        ...member,
        email:      member.profiles?.email      || 'No email',
        full_name:  member.profiles?.full_name  || null,
        avatar_url: member.profiles?.avatar_url || null,
        module_grants: grantsMap[member.id] ?? {},
      }));

      return { data: members as TeamMember[], error: null };
    } catch (error) {
      console.error('getTeamMembers error:', error);
      return { data: null, error };
    }
  },

  // Get user profile by email (RPC call)
  // Returns: UUID string directly (e.g., "feacc8d6-1185-4ee9-b47f-05bafdd362a5")
  async getProfileByEmail(email: string) {
    try {
      const { data, error } = await supabase
        .rpc('get_profile_by_email', { 
          email_param: email.toLowerCase() 
        });

      if (error) {
        console.error('Get profile by email RPC error:', error);
        throw error;
      }

      // RPC returns UUID string directly
      if (!data || typeof data !== 'string') {
        console.error('Invalid RPC response:', data);
        return { 
          data: null, 
          error: { 
            message: 'User not found. They must register for an Axelio account first.',
            code: 'USER_NOT_FOUND'
          } 
        };
      }

      return { data: data as string, error: null };
    } catch (error) {
      console.error('getProfileByEmail error:', error);
      return { data: null, error };
    }
  },

  /**
   * Phase 76.2.D — canonical write path for module role grants.
   *
   * Atomically replaces ALL grants for the given member id (FK column
   * `member_id` on `company_member_module_roles_v76`) with the supplied
   * set: a single DELETE followed by a bulk INSERT.
   *
   * Atomicity is best-effort at the client (Supabase JS has no transaction
   * wrapper for table calls). The BEFORE INSERT/UPDATE trigger from Phase
   * 76.2.F guarantees the subscription invariant: any attempt to grant a role
   * for a module the company is NOT subscribed to is rejected at the DB layer.
   *
   * Pass an empty `grants` array to clear all grants for the member.
   *
   * Returns `{ data: insertedCount, error: null }` on success.
   */
  async replaceMemberModuleRoles(
    companyMemberId: string,
    grants: ModuleRoleGrant[],
  ) {
    try {
      // 1. Clear existing grants for the member.
      const { error: delErr } = await supabase
        .from('company_member_module_roles_v76')
        .delete()
        .eq('member_id', companyMemberId);

      if (delErr) {
        console.error('replaceMemberModuleRoles delete error:', delErr);
        return { data: null, error: delErr };
      }

      // 2. Nothing to insert — done.
      if (grants.length === 0) {
        return { data: 0, error: null };
      }

      // 3. Resolve each (module, level) grant into its role_id uuid by looking
      //    up the canonical role_key in `module_roles_v76`.
      const roleKeys = grants.map((g) => moduleRoleKeyFor(g.module_key, g.level));
      const { data: roleLookup, error: lookupErr } = await buildRoleIdLookup(roleKeys);
      if (lookupErr || !roleLookup) {
        console.error('replaceMemberModuleRoles role-id lookup error:', lookupErr);
        return { data: null, error: lookupErr };
      }

      // Reject up-front if any requested role_key is unknown server-side.
      // (Defense in depth: the BEFORE INSERT/UPDATE trigger from Phase 76.2.F
      // already enforces this, but failing fast yields a cleaner UI error.)
      const missing: string[] = [];
      const rows = grants.map((g) => {
        const rk = moduleRoleKeyFor(g.module_key, g.level);
        const id = roleLookup.get(rk);
        if (!id) missing.push(rk);
        return {
          member_id:  companyMemberId,
          module_key: g.module_key,
          role_id:    id ?? '',
        };
      });
      if (missing.length > 0) {
        const msg = `Unknown role_key(s) in module_roles_v76: ${missing.join(', ')}`;
        console.error('replaceMemberModuleRoles:', msg);
        return { data: null, error: { message: msg, code: 'UNKNOWN_ROLE_KEY' } };
      }

      // 4. Bulk insert the new grant set.
      const { data, error: insErr } = await supabase
        .from('company_member_module_roles_v76')
        .insert(rows)
        .select('id');

      if (insErr) {
        console.error('replaceMemberModuleRoles insert error:', insErr);
        return { data: null, error: insErr };
      }

      return { data: data?.length ?? 0, error: null };
    } catch (error) {
      console.error('replaceMemberModuleRoles error:', error);
      return { data: null, error };
    }
  },

  /**
   * Read back the canonical grants for a member as a matrix-shaped record.
   * Used by the edit modal to hydrate <PermissionsMatrix /> from the v76
   * source of truth instead of the legacy JSON column.
   *
   * If a member has no v76 grants yet (un-migrated row), returns an empty
   * record; the caller is responsible for the back-compat fallback to
   * `company_members.module_permissions`.
   */
  async getMemberModuleRoles(
    companyMemberId: string,
  ): Promise<{
    data: Record<string, ModuleRoleLevel | 'none'>;
    error: any;
  }> {
    try {
      // Phase 76.2.E hotfix: the grant table's role identifier is `role_id`
      // (uuid FK to module_roles_v76.id), not a text column. We JOIN
      // `module_roles_v76` via the PostgREST embedded-resource syntax to pull
      // the canonical text `role_key` back in a single round-trip.
      const { data, error } = await supabase
        .from('company_member_module_roles_v76')
        .select('module_key, role_id, module_roles_v76:role_id(role_key)')
        .eq('member_id', companyMemberId);

      if (error) {
        console.error('getMemberModuleRoles error:', error);
        return { data: {}, error };
      }

      const out: Record<string, ModuleRoleLevel | 'none'> = {};
      for (const row of data ?? []) {
        const mod = (row as { module_key: string }).module_key;
        const embed = (row as {
          module_roles_v76?: { role_key?: string } | Array<{ role_key?: string }> | null;
        }).module_roles_v76;
        const roleK = Array.isArray(embed)
          ? embed[0]?.role_key ?? ''
          : embed?.role_key ?? '';
        // role_key convention: `${module_key}.${level}` — extract suffix after the dot.
        const suffix = roleK.startsWith(`${mod}.`) ? roleK.slice(mod.length + 1) : roleK;
        if (suffix === 'admin' || suffix === 'editor' || suffix === 'viewer' || suffix === 'manager' || suffix === 'employee') {
          out[mod] = suffix as ModuleRoleLevel;
        }
      }
      return { data: out, error: null };
    } catch (error) {
      console.error('getMemberModuleRoles error:', error);
      return { data: {}, error };
    }
  },

  /**
   * Invite a team member to a company.
   *
   * Works for both registered and unregistered users:
   * - Always creates a `company_invitations` row so the invitation is tracked
   *   and the auto-accept DB trigger can fire on signup.
   * - If the user is already registered: also creates the company_members row
   *   and assigns module grants immediately (direct access, no wait required).
   * - If the user is not registered: the invitation row is enough — the
   *   handle_new_user() trigger will create the membership automatically
   *   when they sign up.
   * - In both cases, an invitation email is dispatched via the
   *   `send-invitation` edge function.
   */
  async inviteTeamMember(inviteData: InviteTeamMemberData) {
    try {
      const email = inviteData.email.toLowerCase().trim();

      // Build the module_roles jsonb for the invitation row (module -> role_key suffix).
      const moduleRolesForInvite: Record<string, string> = {};
      for (const grant of (inviteData.module_role_grants ?? [])) {
        moduleRolesForInvite[grant.module_key] = grant.level;
      }

      // Step 1: Always upsert a company_invitations row.
      // Using upsert on (company_id, email, status='pending') to avoid duplicates.
      const { data: invitation, error: inviteInsertErr } = await supabase
        .from('company_invitations')
        .upsert(
          {
            company_id:      inviteData.company_id,
            email,
            status:          'pending',
            membership_role: inviteData.role,
            module_roles:    moduleRolesForInvite,
          },
          { onConflict: 'company_id,email,status' },
        )
        .select()
        .maybeSingle();

      if (inviteInsertErr) {
        console.error('Invitation insert error:', inviteInsertErr);
        return { data: null, error: { message: inviteInsertErr.message, code: 'INVITE_INSERT_ERROR', details: inviteInsertErr } };
      }

      // Step 2: Check if the user is already registered.
      const { data: userId } = await this.getProfileByEmail(email);

      if (userId) {
        // Registered user path — check for existing membership.
        const { data: existingMember } = await supabase
          .from('company_members')
          .select('id')
          .eq('company_id', inviteData.company_id)
          .eq('user_id', userId)
          .maybeSingle();

        if (existingMember) {
          return {
            data: null,
            error: { message: 'This user is already a member of the workspace.', code: 'ALREADY_MEMBER' }
          };
        }

        // Create the membership immediately.
        const { data: member, error: memberError } = await supabase
          .from('company_members')
          .insert({
            company_id:         inviteData.company_id,
            user_id:            userId,
            role:               inviteData.role,
            module_permissions: null,
          })
          .select()
          .single();

        if (memberError) {
          console.error('Member insert error:', memberError);
          return { data: null, error: { message: memberError.message, code: memberError.code ?? 'INSERT_ERROR', details: memberError } };
        }

        // Assign module grants for the new member.
        const grants = inviteData.role === 'member' ? (inviteData.module_role_grants ?? []) : [];
        const { error: grantErr } = await this.replaceMemberModuleRoles(
          (member as { id: string }).id,
          grants,
        );

        if (grantErr) {
          console.error('Module role grant insert error:', grantErr);
          await supabase.from('company_members').delete().eq('id', (member as { id: string }).id);
          return { data: null, error: { message: (grantErr as { message?: string }).message ?? 'Failed to set module permissions', code: (grantErr as { code?: string }).code ?? 'GRANT_INSERT_ERROR', details: grantErr } };
        }

        // Mark the invitation as accepted since user is already in.
        if (invitation?.id) {
          await supabase
            .from('company_invitations')
            .update({ status: 'accepted' })
            .eq('id', invitation.id);
        }
      }

      // Step 3: Dispatch invitation email (fire-and-forget, non-blocking).
      const inviteToken = (invitation as { token?: string } | null)?.token;
      if (inviteToken) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
        fetch(`${supabaseUrl}/functions/v1/send-invitation`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${anonKey}` },
          body:    JSON.stringify({
            email,
            invite_token:  inviteToken,
            company_id:    inviteData.company_id,
            is_registered: !!userId,
          }),
        }).catch((e) => console.warn('[teamService] send-invitation fire-and-forget error:', e));
      }

      return { data: invitation, error: null };
    } catch (error: any) {
      console.error('inviteTeamMember error:', error);
      return { data: null, error: { message: error?.message || 'An unexpected error occurred', code: 'UNKNOWN_ERROR', details: error } };
    }
  },

  // Remove team member from company
  async removeTeamMember(memberId: string) {
    try {
      // Phase 76.2.D — drop the member's grants first so the parent delete
      // doesn't trip any future FK constraint that might be added. This is
      // safe even if there are no rows.
      await supabase
        .from('company_member_module_roles_v76')
        .delete()
        .eq('member_id', memberId);

      const { error } = await supabase
        .from('company_members')
        .delete()
        .eq('id', memberId);

      if (error) {
        console.error('Remove team member error:', error);
        throw error;
      }

      return { error: null };
    } catch (error) {
      console.error('removeTeamMember error:', error);
      return { error };
    }
  },

  /**
   * Update team member role and per-module grants.
   *
   * Phase 76.2.D: the canonical write goes against `company_member_module_roles_v76`.
   * The legacy `module_permissions` JSON column is NO LONGER written on update —
   * it is also explicitly cleared so stale JSON cannot diverge from the v76 grant
   * set during the cutover window.
   */
  async updateTeamMember(
    memberId: string, 
    role: 'admin' | 'member' | 'viewer',
    grants?: ModuleRoleGrant[],
  ) {
    try {
      // 1. Update the member's role + clear legacy JSON column.
      const { data, error } = await supabase
        .from('company_members')
        .update({
          role,
          module_permissions: null,
        })
        .eq('id', memberId)
        .select()
        .single();

      if (error) {
        console.error('Update team member error:', error);
        throw error;
      }

      // 2. Replace the canonical grant set. For admins we drop all grants
      //    (the role column governs full access); for members we use the
      //    supplied grants (an empty array clears them, which is valid).
      const effectiveGrants = role === 'member' ? (grants ?? []) : [];
      const { error: grantErr } = await this.replaceMemberModuleRoles(
        memberId,
        effectiveGrants,
      );

      if (grantErr) {
        console.error('Update module role grants error:', grantErr);
        return { data: null, error: grantErr };
      }

      return { data, error: null };
    } catch (error) {
      console.error('updateTeamMember error:', error);
      return { data: null, error };
    }
  },
};
